import React, { useEffect, useState } from 'react';
import { Star, Edit, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTestimonials = () => {
    axios.get(`${API}/testimonials?published_only=false`)
      .then(res => setTestimonials(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.rating = parseInt(data.rating);
    data.published = data.published === 'on';

    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, data, { headers });
        toast.success('Bewertung aktualisiert!');
      } else {
        await axios.post(`${API}/testimonials`, data, { headers });
        toast.success('Bewertung erstellt!');
      }
      setIsDialogOpen(false);
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchtest du diese Bewertung wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers });
      toast.success('Bewertung gelöscht!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const togglePublished = async (testimonial) => {
    try {
      await axios.put(`${API}/testimonials/${testimonial.id}`, { published: !testimonial.published }, { headers });
      toast.success(testimonial.published ? 'Bewertung versteckt' : 'Bewertung veröffentlicht');
      fetchTestimonials();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bewertungen</h1>
          <p className="text-slate-600">Verwalte Kundenbewertungen für die Website.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingTestimonial(null)} data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4 mr-2" /> Neue Bewertung
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTestimonial ? 'Bewertung bearbeiten' : 'Neue Bewertung'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" defaultValue={editingTestimonial?.name} required />
                </div>
                <div>
                  <Label htmlFor="location">Standort</Label>
                  <Input id="location" name="location" defaultValue={editingTestimonial?.location} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rating">Bewertung</Label>
                  <Select name="rating" defaultValue={String(editingTestimonial?.rating || 5)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5,4,3,2,1].map(r => (
                        <SelectItem key={r} value={String(r)}>{r} Sterne</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="license_type">Führerscheinklasse</Label>
                  <Input id="license_type" name="license_type" defaultValue={editingTestimonial?.license_type || 'B'} />
                </div>
              </div>
              <div>
                <Label htmlFor="text">Bewertungstext</Label>
                <Textarea id="text" name="text" rows={4} defaultValue={editingTestimonial?.text} required />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="published" 
                  name="published" 
                  defaultChecked={editingTestimonial?.published ?? true}
                  className="rounded"
                />
                <Label htmlFor="published">Veröffentlicht</Label>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">Speichern</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id} data-testid={`testimonial-item-${testimonial.id}`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => togglePublished(testimonial)}
                  >
                    {testimonial.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => { setEditingTestimonial(testimonial); setIsDialogOpen(true); }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-slate-600 mb-4">"{testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.location} • Klasse {testimonial.license_type}</p>
                </div>
                <span className={`badge ${testimonial.published ? 'badge-success' : 'badge-warning'}`}>
                  {testimonial.published ? 'Sichtbar' : 'Versteckt'}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          Noch keine Bewertungen vorhanden.
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
